/**
 * "Importer fra lenke" — writer-only entry point for the URL recipe import
 * flow (Steg 5 slice 2). The user pastes a link to a recipe page on another
 * site; the server fetches it (SSRF-safe), extracts a structured recipe
 * (preferring schema.org/Recipe JSON-LD, falling back to an LLM reading the
 * page), and comes back as an unsaved, editor-ready DRAFT — see `api.ts`'s
 * `startUrlImport`/`getImportJob` and `RecipeImportService` on the API side.
 *
 * REVIEW-BEFORE-SAVE: this component NEVER persists a recipe itself — it only
 * hands the extracted `RecipeImportDraft` to `onImported`, which the caller
 * uses to navigate into the editor (prefilled, unsaved — see
 * `RecipeEditorPage`'s import-draft seeding for the actual save gate). A url
 * import may carry a `heroImageKey` (a source hero image the server found and
 * copied into cookbook-scoped storage) — the editor seeds it onto the new
 * recipe on save.
 *
 * Sequence on submit: a cheap client-side format check (non-empty, parses as
 * an http(s) URL — the real SSRF-relevant validation is entirely the
 * server's `safeFetch` job) → start an ASYNC import job → POLL its status
 * until `done`/`failed`/the client's attempt cap, via the SAME
 * `pollImportJob` helper `PhotoImportButton` uses (`../lib/importPoll`), so
 * both sources behave identically.
 *
 * UI: collapsed to a single toggle button by default (matches
 * `PhotoImportButton`'s footprint in the sjef-bar); clicking it reveals an
 * inline `<input type="url">` + submit. Busy disables re-submit; every
 * failure surfaces a short Norwegian inline message and never throws.
 */

import type { RecipeImportDraft } from "@tjommi/shared";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";

import {
  ForbiddenError,
  InvalidImportUrlError,
  NotFoundError,
  UnauthorizedError,
  startUrlImport,
} from "../api";
import {
  ImportJobFailedError,
  ImportPollCancelledError,
  ImportPollTimeoutError,
  pollImportJob,
} from "../lib/importPoll";

/**
 * A cheap client-side format check — non-empty and parses as an http(s) url.
 * The server's `safeFetch` (scheme + resolved-address allow-list, redirect
 * revalidation) does the real SSRF-relevant validation; this only avoids a
 * round-trip for an obviously blank/garbage input.
 */
function looksLikeHttpUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export function UrlImportForm({
  cookbookId,
  accessToken,
  onSessionExpired,
  onImported,
  toggleClassName = "sjef-import-btn",
  toggleLabel = "Importer fra lenke",
  submitBusyLabel = "Henter oppskriften …",
  testId = "url-import",
  pollIntervalMs,
  maxPollAttempts,
  onBusyChange,
  defaultOpen = false,
  onCancel,
  submitLabel = "Hent",
  inputLabel,
  hint,
}: {
  cookbookId: string;
  accessToken: string;
  onSessionExpired: () => void;
  /** The caller navigates into the editor with this draft (review-before-save). */
  onImported: (draft: RecipeImportDraft) => void;
  toggleClassName?: string;
  toggleLabel?: string;
  submitBusyLabel?: string;
  testId?: string;
  /** Injectable for tests — how often to poll the job status. */
  pollIntervalMs?: number;
  /** Injectable for tests — how many polls before giving up client-side. */
  maxPollAttempts?: number;
  /** Reports whether an import is in flight, so a host sheet can refuse to
   * close (and so discard it) meanwhile. */
  onBusyChange?: (busy: boolean) => void;
  /** Start in the revealed (form) state — used by the home page's "Ny
   * oppskrift" sheet, which has already asked "from a web page?". */
  defaultOpen?: boolean;
  /** When set, "Avbryt" calls this (the host closes/steps back) instead of
   * collapsing to the toggle. */
  onCancel?: () => void;
  submitLabel?: string;
  /** A visible label for the input (replaces the aria-label). */
  inputLabel?: string;
  /** Helper text shown under the input. */
  hint?: React.ReactNode;
}): React.JSX.Element {
  const inputId = useId();
  const [open, setOpen] = useState(defaultOpen);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!busy) return;
    onBusyChange?.(true);
    return () => onBusyChange?.(false);
  }, [busy]);
  const [error, setError] = useState<string | null>(null);

  // Same race-safety pattern as PhotoImportButton: a run token, checked
  // before acting on an async result, so an unmount or a newer submit never
  // lets a stale poll loop call setState / onImported after the fact.
  const runIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
      e.preventDefault();
      setError(null);
      if (!looksLikeHttpUrl(url)) {
        setError("Ugyldig lenke.");
        return;
      }
      const runId = ++runIdRef.current;
      const isActive = () => mountedRef.current && runIdRef.current === runId;
      setBusy(true);
      try {
        const { jobId } = await startUrlImport(cookbookId, url.trim(), accessToken);
        const draft = await pollImportJob(cookbookId, jobId, accessToken, {
          intervalMs: pollIntervalMs,
          maxAttempts: maxPollAttempts,
          isActive,
        });
        if (isActive()) {
          onImported(draft);
          if (!defaultOpen) setOpen(false);
          setUrl("");
        }
      } catch (err) {
        if (err instanceof ImportPollCancelledError) return; // a newer submit / unmount
        if (err instanceof UnauthorizedError) {
          onSessionExpired();
          return;
        }
        if (err instanceof ForbiddenError) {
          setError("Ingen skrivetilgang.");
          return;
        }
        if (err instanceof InvalidImportUrlError) {
          setError("Ugyldig lenke.");
          return;
        }
        if (err instanceof NotFoundError) {
          setError("Importen kunne ikke finnes igjen. Prøv igjen.");
          return;
        }
        if (err instanceof ImportJobFailedError) {
          setError(err.serverMessage ?? "Klarte ikke å hente oppskriften fra lenken.");
          return;
        }
        if (err instanceof ImportPollTimeoutError) {
          setError("Importen tok for lang tid. Prøv igjen.");
          return;
        }
        setError(err instanceof Error ? err.message : "Noe gikk galt. Prøv igjen.");
      } finally {
        if (mountedRef.current && runIdRef.current === runId) setBusy(false);
      }
    },
    [
      accessToken,
      cookbookId,
      defaultOpen,
      maxPollAttempts,
      onImported,
      onSessionExpired,
      pollIntervalMs,
      url,
    ],
  );

  if (!open) {
    return (
      <button
        type="button"
        className={toggleClassName}
        data-testid={testId}
        onClick={() => setOpen(true)}
      >
        {toggleLabel}
      </button>
    );
  }

  return (
    <form
      className="url-import-form"
      data-testid={`${testId}-form`}
      // We do our own (more permissive, and more informative) url validation
      // in handleSubmit — the native `type="url"` constraint validation would
      // otherwise silently swallow the submit event for anything it deems
      // malformed (e.g. a bare "not a url") before our handler ever runs.
      noValidate
      onSubmit={(e) => void handleSubmit(e)}
    >
      {inputLabel && (
        <label className="url-import-label" htmlFor={inputId}>
          {inputLabel}
        </label>
      )}
      <input
        id={inputId}
        type="url"
        className="url-import-input"
        placeholder="Lim inn lenke til en oppskrift …"
        aria-label={inputLabel ? undefined : "Lenke til oppskrift"}
        autoFocus={defaultOpen}
        data-testid={`${testId}-input`}
        value={url}
        disabled={busy}
        onChange={(e) => setUrl(e.target.value)}
      />
      {hint && <p className="url-import-hint">{hint}</p>}
      <button
        type="submit"
        className="url-import-submit"
        data-testid={`${testId}-submit`}
        disabled={busy}
      >
        {busy ? submitBusyLabel : submitLabel}
      </button>
      <button
        type="button"
        className="url-import-cancel"
        data-testid={`${testId}-cancel`}
        disabled={busy}
        onClick={() => {
          setError(null);
          setUrl("");
          if (onCancel) onCancel();
          else setOpen(false);
        }}
      >
        Avbryt
      </button>
      {error && (
        <p className="image-error" data-testid={`${testId}-error`} role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
